import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import MainNavPanel from './MainNavPanel';
import ExerciseNavPanel from './ExerciseNavPanel';
import TaskManagementNavPanel from './TaskManagementNavPanel';
import { useClickAway } from 'react-use';
import { useSidebarNavContext } from './SidebarNavProvider';
import dayjs from 'dayjs';
import IncidentWorkspacePanel from './IncidentWorkspacePanel';
import { useLocation, useMatch } from 'react-router';
import NotificationsNavPanel from './NotificationsNavPanel';
import ProductToursNavPanel from './ProductToursNavPanel';
import { useTransitionState } from 'react-transition-state';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { useHwcgFlag } from 'utils/featureFlags';
import { registerHwcgTourApi } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';

export type PanelType =
  | 'ics'
  | 'exercise'
  | 'task'
  | 'notifications'
  | 'tours'
  | null;

/** TODO: check scrollbars in Windows/Firefox/Safari */
const SidebarNav: React.FC<{
  toggleRef: MutableRefObject<HTMLButtonElement | null>;
}> = ({ toggleRef }) => {
  const { close, open, isOpen, stuck, setStuck } = useSidebarNavContext();
  const hwcg = useHwcgFlag();
  const stickOpen = hwcg && stuck && isOpen;
  const stuckRef = useRef(stuck);
  stuckRef.current = stuck;
  const openRef = useRef(open);
  openRef.current = open;
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!hwcg) return;
    return registerHwcgTourApi({
      openMenu: () => openRef.current(),
      closeMenu: () => {
        if (!stuckRef.current) closeRef.current();
      },
    });
  }, [hwcg]);
  const location = useLocation();

  const iapMatch = useMatch({ path: '/iap', end: false });
  const taskMatch = useMatch({ path: '/task_management', end: false });
  const prepareMatch = useMatch({ path: '/prepare', end: false });
  const notificationsMatch = useMatch({ path: '/notifications', end: false });
  const scenariosMatch = useMatch({ path: '/scenarios', end: false });
  const mapMatch = useMatch({ path: '/hub', end: false });

  const [secondPanel, setSecondPanel] = useState<PanelType>(() => {
    if (iapMatch) return 'ics';
    if (taskMatch) return 'task';
    if (prepareMatch) return 'exercise';
    // Scenarios sit outside /notifications but are reached from its panel, so
    // landing on one directly should not collapse the sidebar back to the root.
    if (notificationsMatch || scenariosMatch) return 'notifications';
    return null;
  });
  // Only the transition into an incident or the hub slides the menu. Forcing
  // it on every render would undo Back, and on the hub it would close Product
  // Tours as soon as they open.
  const wasOnIapRef = useRef(Boolean(iapMatch));
  const wasOnHubRef = useRef(Boolean(mapMatch));

  const navRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle('sidebar-stuck', stickOpen);
    return () => document.body.classList.remove('sidebar-stuck');
  }, [stickOpen]);

  useClickAway(navRef, (e) => {
    if (!isOpen || stickOpen) return;
    const target = e.target;
    if (!(target instanceof Node)) return;
    if (toggleRef.current?.contains(target)) return;
    // Workspace Selects portal their menu outside #sidebar-nav. Ignore those
    // hits so mousedown-to-choose is not treated as click-away.
    if (
      target instanceof Element &&
      target.closest('[data-slot="select-content"]')
    ) {
      return;
    }
    close();
  });

  useEffect(() => {
    const onIap = Boolean(iapMatch);
    const onHub = Boolean(mapMatch);
    const enteringHub = onHub && !wasOnHubRef.current;
    const enteringIap = onIap && !wasOnIapRef.current;
    if (enteringHub || enteringIap) {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest('#sidebar-nav')) {
        active.blur();
      }
    }
    if (enteringHub) {
      setSecondPanel(null);
    } else if (enteringIap) {
      setSecondPanel('ics');
    }
    wasOnIapRef.current = onIap;
    wasOnHubRef.current = onHub;
  }, [location.pathname, iapMatch, mapMatch]);

  // Track which second panel to render (persists during exit animation)
  const [renderedPanel, setRenderedPanel] = useState<PanelType>(secondPanel);

  const showSecondPanel = secondPanel !== null;

  const [{ status }, toggle] = useTransitionState({
    timeout: 300,
    preEnter: true,
    preExit: true,
    initialEntered: showSecondPanel,
  });

  useEffect(() => {
    toggle(showSecondPanel);
  }, [showSecondPanel, toggle]);

  // Update rendered panel when a new second panel is selected
  useEffect(() => {
    if (secondPanel !== null) {
      setRenderedPanel(secondPanel);
    }
  }, [secondPanel]);

  // Apply slider class during enter/entered/preExit phases
  const isSliderActive =
    status === 'entering' || status === 'entered' || status === 'preExit';

  const openPanel = (key: PanelType) => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('#sidebar-nav')) {
      active.blur();
    }
    setSecondPanel(key);
  };

  // Keep second panel content mounted until exit animation completes
  const shouldRenderSecondPanel = status !== 'exited' && renderedPanel !== null;

  return (
    <div
      id="sidebar-nav"
      className={cn(
        'dark tw:bg-popover tw:text-popover-foreground tw:border-r tw:border-border tw:shadow-lg',
        isOpen && 'open',
        hwcg && 'sidebar-nav--stickable'
      )}
      ref={navRef}
    >
      <div className="sidebar-nav-content">
        <div
          className={cn(
            'sidebar-nav-slider',
            isSliderActive && 'show-second-panel'
          )}
        >
          <div className="sidebar-nav-panel" aria-hidden={isSliderActive}>
            <MainNavPanel
              setSecondPanel={openPanel}
            />
          </div>
          <div className="sidebar-nav-panel" aria-hidden={!isSliderActive}>
            {shouldRenderSecondPanel && (
              <SecondPanelSwitch
                secondPanel={renderedPanel}
                navBack={() => openPanel(null)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="sidebar-nav-footer flex-shrink-0 flex-grow-0">
        <div className="tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-2">
          {hwcg ? (
            <label
              className="tw:flex tw:w-full tw:items-center tw:justify-start tw:gap-2 tw:text-sm tw:text-foreground"
              data-tour="hwcg-stick"
            >
              <Switch
                checked={stuck}
                onCheckedChange={(checked) => setStuck(checked)}
                aria-label="Stick"
              />
              <span className="sidebar-stick-text">Stick</span>
            </label>
          ) : null}
          <span className="tw:w-full tw:text-center">
            © {dayjs().format('YYYY')} PRATUS • All rights reserved.
          </span>
        </div>
      </div>
    </div>
  );
};

export default SidebarNav;

const SecondPanelSwitch: React.FC<{
  secondPanel: PanelType;
  navBack: () => void;
}> = ({ secondPanel, navBack }) => {
  switch (secondPanel) {
    case 'exercise':
      return <ExerciseNavPanel navBack={navBack} />;
    case 'task':
      return <TaskManagementNavPanel navBack={navBack} />;
    case 'ics':
      return <IncidentWorkspacePanel navBack={navBack} />;
    case 'notifications':
      return <NotificationsNavPanel navBack={navBack} />;
    case 'tours':
      return <ProductToursNavPanel navBack={navBack} />;
    default:
      return null;
  }
};
